import * as Yup from "yup";

export const acquirerInitialValues = {
  version: "04.09.01",
  acquirerName: "",
  withISO: true,
  email: "",
  acquirer: {
    city: "",
    state: "",
    county: "",
    address: "",
    postalCode: "",
    countryCode: "",
    neighborhood: "",
  },
  fiat: {
    currencyCode: "",
  },
  blokko: {
    effectiveDate: "",
    amountTxProcessing: "",
    amountTerminalSetup: "0.00",
    percentTxProcessing: "",
    amountTxProcessingTiers: "",
    amountTerminalSetupTiers: "",
    percentTxProcessingTiers: "",
    amountTerminalMaintenance: "0.00",
    terminalMaintenanceInactive: false,
    amountTerminalMaintenanceTiers: "",
  },
  bank: {
    Iban: "",
    swiftCode: "",
    routingNumber: "",
    accountNumber: "",
    branchNumber: "",
    Bid: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    countryCode: "st",
  },
  crypto_wallets: [],
  timeStamp: "",
  reqhashMAC: "",
};

const acquirerSharedValidationFields = {
  // Acquirer Info Step
  email: Yup.string().email("Invalid email address").max(
    100,
    "Email cannot exceed 100 characters",
  ),

  acquirerName: Yup.string()
    .required("Acquirer name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name cannot exceed 50 characters"),

  acquirer: Yup.object().shape({
    city: Yup.string()
      .required("City is required")
      .min(2, "City must be at least 2 characters")
      .max(50, "City cannot exceed 50 characters"),

    state: Yup.string()
      .required("State is required")
      .min(2, "State must be at least 2 characters")
      .max(30, "State cannot exceed 30 characters"),

    county: Yup.string()
      .min(2, "County must be at least 2 characters")
      .max(16, "County cannot exceed 16 characters"),

    address: Yup.string()
      .required("Address is required")
      .min(5, "Address must be at least 5 characters")
      .max(200, "Address cannot exceed 200 characters"),

    postalCode: Yup.string()
      .required("Postal code is required")
      .min(3, "Postal code must be at least 3 characters")
      .max(20, "Postal code cannot exceed 20 characters"),

    countryCode: Yup.string()
      .required("Country code is required")
      .min(2, "Country code must be at least 2 characters")
      .length(2, "Country code must be exactly 2 characters"),
  }),

  fiat: Yup.object().shape({
    currencyCode: Yup.string()
      .required("Currency code is required")
      .matches(/^[A-Z]{3}$/, "Please select a valid currency"),
  }),

  // Acquirer Commissions Step
  blokko: Yup.object().shape({
    amountTxProcessing: Yup.number()
      .typeError("Transaction processing amount must be a number")
      .required("Transaction processing amount is required")
      .min(0, "Transaction processing amount must be 0 or greater")
      .test(
        "decimal-places",
        "Amount must be an integer or have at most 2 decimal places (e.g., 0, 0.00, 1, 1.0, 1.00, 0.10)",
        (value) => {
          if (value === null || value === undefined) return false;

          const valueStr = value.toString();

          // Si no hay punto decimal → es entero → válido
          if (!valueStr.includes(".")) {
            return true;
          }

          // Si hay punto decimal → debe tener máximo 2 decimales
          const decimalPlaces = valueStr.split(".")[1];
          const isValid = decimalPlaces && decimalPlaces.length <= 2;

          return isValid;
        },
      ),

    percentTxProcessing: Yup.number()
      .typeError("Transaction processing percentage must be a number")
      .required("Transaction processing percentage is required")
      .min(0, "Transaction processing percentage must be 0 or greater")
      .test(
        "decimal-places",
        "Percentage must be an integer or have at most 4 decimal places (e.g., 0, 0.0, 0.00, 20, 20.0, 20.00, 20.000, 20.0000, 0.5000)",
        (value) => {
          if (value === null || value === undefined) return false;

          const valueStr = value.toString();

          // Si no hay punto decimal → es entero → válido
          if (!valueStr.includes(".")) {
            return true;
          }

          // Si hay punto decimal → debe tener máximo 4 decimales
          const decimalPlaces = valueStr.split(".")[1];
          const isValid = decimalPlaces && decimalPlaces.length <= 4;

          return isValid;
        },
      ),
  }),

  // Bank Info Step
  bank: Yup.object().shape({
    Iban: Yup.string()
      .required("IBAN is required")
      .min(4, "IBAN must be at least 4 characters")
      .max(40, "IBAN cannot exceed 40 characters"),

    routingNumber: Yup.string()
      .required("Routing number is required")
      // .length(9, "Routing number must be exactly 9 digits")
      // .matches(/^\d{9}$/, "Routing number must contain only digits"),
      .max(12, "Routing number cannot exceed 12 characters"),

    accountNumber: Yup.string()
      .required("Account number is required")
      .min(5, "Account number must be at least 5 characters")
      .max(18, "Account number cannot exceed 18 characters"),

    address: Yup.string()
      .required("Bank address is required")
      .min(5, "Address must be at least 5 characters")
      .max(32, "Address cannot exceed 32 characters"),
  }),
};

export const acquirerCreateValidationSchema = Yup.object().shape({
  ...acquirerSharedValidationFields,
  email: acquirerSharedValidationFields.email.required("Email is required"),
});

export const acquirerUpdateValidationSchema = Yup.object().shape({
  ...acquirerSharedValidationFields,
  email: acquirerSharedValidationFields.email.notRequired(),
});

// Backward compatibility for existing imports.
export const acquirerInitialValidationSchema = acquirerCreateValidationSchema;

export const setAcquirerValues = ({ acquirerData, setFieldValue }) => {
  console.log("Setting form values from acquirerData:", acquirerData);

  // Set acquirer info fields (including withISO and email for validation / create user logic)
  setFieldValue("acquirerName", acquirerData.acquirerName || "", true);
  setFieldValue("email", acquirerData.email || "", true);
  const withISOValue = acquirerData.withISO ?? acquirerData.with_iso;
  setFieldValue(
    "withISO",
    withISOValue === true || withISOValue === "true",
    true,
  );
  setFieldValue("acquirer.address", acquirerData.acquirer?.address || "", true);
  setFieldValue("acquirer.city", acquirerData.acquirer?.city || "", true);
  setFieldValue("acquirer.county", acquirerData.acquirer?.county || "", true);
  setFieldValue("acquirer.state", acquirerData.acquirer?.state || "", true);
  setFieldValue(
    "acquirer.postalCode",
    acquirerData.acquirer?.postalCode || "",
    true,
  );
  setFieldValue(
    "acquirer.countryCode",
    acquirerData.acquirer?.countryCode || "",
    true,
  );

  // Set fiat currency field
  setFieldValue(
    "fiat.currencyCode",
    acquirerData.fiat?.currencyCode || "",
    true,
  );

  // Set blokko commission fields
  setFieldValue(
    "blokko.effectiveDate",
    acquirerData.blokko?.effectiveDate || "",
    true,
  );
  // Formatear automáticamente los números con decimales apropiados
  const amountValue = parseFloat(acquirerData.blokko?.amountTxProcessing) || 0;
  const percentValue =
    parseFloat(acquirerData.blokko?.percentTxProcessing) || 0;

  // Formatear amountTxProcessing con 2 decimales
  const formattedAmount = amountValue.toFixed(2);

  // Formatear percentTxProcessing con 4 decimales
  const formattedPercent = percentValue.toFixed(4);

  setFieldValue("blokko.amountTxProcessing", formattedAmount, true);
  setFieldValue("blokko.percentTxProcessing", formattedPercent, true);

  console.log(
    "amountTxProcessing - Original:",
    acquirerData.blokko?.amountTxProcessing,
    "Parsed:",
    amountValue,
    "Formatted:",
    formattedAmount,
    "Type:",
    typeof formattedAmount,
  );
  console.log(
    "percentTxProcessing - Original:",
    acquirerData.blokko?.percentTxProcessing,
    "Parsed:",
    percentValue,
    "Formatted:",
    formattedPercent,
    "Type:",
    typeof formattedPercent,
  );
  setFieldValue(
    "blokko.amountTerminalSetup",
    acquirerData.blokko?.amountTerminalSetup || "",
    true,
  );
  setFieldValue(
    "blokko.amountTxProcessingTiers",
    acquirerData.blokko?.amountTxProcessingTiers || "",
    true,
  );
  setFieldValue(
    "blokko.amountTerminalSetupTiers",
    acquirerData.blokko?.amountTerminalSetupTiers || "",
    true,
  );
  setFieldValue(
    "blokko.amountTerminalMaintenance",
    acquirerData.blokko?.amountTerminalMaintenance || "0.00",
    true,
  );
  setFieldValue(
    "blokko.terminalMaintenanceInactive",
    acquirerData.blokko?.terminalMaintenanceInactive || false,
    true,
  );
  setFieldValue(
    "blokko.amountTerminalMaintenanceTiers",
    acquirerData.blokko?.amountTerminalMaintenanceTiers || "",
    true,
  );

  // Set bank info fields if bank data exists
  if (acquirerData.bank) {
    setFieldValue("bank.Iban", acquirerData.bank.Iban || "", true);
    setFieldValue("bank.address", acquirerData.bank.address || "", true);
    setFieldValue(
      "bank.accountNumber",
      acquirerData.bank.accountNumber || "",
      true,
    );
    setFieldValue(
      "bank.routingNumber",
      acquirerData.bank.routingNumber || "",
      true,
    );
    setFieldValue("bank.swiftCode", acquirerData.bank.swiftCode || "", true);
    setFieldValue(
      "bank.branchNumber",
      acquirerData.bank.branchNumber || "",
      true,
    );
    setFieldValue("bank.Bid", acquirerData.bank.Bid || "", true);
    setFieldValue("bank.city", acquirerData.bank.city || "", true);
    setFieldValue("bank.state", acquirerData.bank.state || "", true);
    setFieldValue("bank.postalCode", acquirerData.bank.postalCode || "", true);
    setFieldValue(
      "bank.countryCode",
      acquirerData.bank.countryCode || "st",
      true,
    );
  }

  // Set crypto wallets fields if crypto wallets data exists
  if (acquirerData.crypto_wallets) {
    setFieldValue("crypto_wallets", acquirerData.crypto_wallets || [], true);
  }
};
